// Narrative template picker — inline picker for time-entry description
// fields.  Fetches the caller's + firm-wide templates from
// /api/narrative-templates.  Filters by the current activity_code when
// set (templates bound to a specific code appear first).  On pick,
// replaces the description text and fires POST /:id/use to bump the
// use_count (fire-and-forget).
//
// Props:
//   activityCode: current activity_code on the form (optional filter)
//   value:        current description text
//   onPick:       (newText) => void
//   className:    optional wrapper class
//
// This file ALSO exports a named `NarrativeAutocomplete` component
// (G11): a typing-aware wrapper that pops up a dropdown of matching
// templates once the field has ≥3 chars, keyed to activity_code, with
// keyboard navigation (Down/Up + Tab/Enter accept, Escape dismiss).
// Wired into TimeEntries manual entry, the global timer popup, and the
// Electron pop-out timer.  Complementary to the button-mode picker —
// both surfaces stay available so users who prefer clicking a menu
// still get one.

import { useEffect, useMemo, useRef, useState } from 'react'
import api from '../services/api'

export default function NarrativeTemplatePicker ({ activityCode, value, onPick, className = '' }) {
  const [templates, setTemplates] = useState([])
  const [open, setOpen] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [filter, setFilter] = useState('')
  const wrapRef = useRef(null)

  // Lazy-load on first open so we don't hit the API when the form is
  // just sitting there.
  const openPicker = async () => {
    setOpen(true)
    if (loaded) return
    try {
      const r = await api.get('/api/narrative-templates', {
        params: activityCode ? { activity_code: activityCode } : {}
      })
      setTemplates(r.data.templates || [])
    } catch { setTemplates([]) } finally { setLoaded(true) }
  }

  // Click-outside to close
  useEffect(() => {
    if (!open) return
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const pick = async (t) => {
    onPick(t.body)
    setOpen(false)
    // Fire-and-forget: bump use_count so frequently-used templates rise
    api.post(`/api/narrative-templates/${t.id}/use`).catch(() => {})
  }

  const filtered = filter.trim()
    ? templates.filter(t => {
        const q = filter.toLowerCase()
        return (t.label || '').toLowerCase().includes(q) || (t.body || '').toLowerCase().includes(q)
      })
    : templates

  return (
    <div ref={wrapRef} className={`inline-block relative ${className}`}>
      <button type="button" onClick={openPicker}
        title="Insert from a saved narrative template"
        className="text-xs text-primary-700 hover:text-primary-900 hover:underline">
        📝 Templates
      </button>
      {open && (
        <div className="absolute z-30 mt-1 right-0 w-96 max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input autoFocus type="text" value={filter} onChange={e => setFilter(e.target.value)}
              placeholder="Filter templates…"
              className="w-full text-xs border border-gray-300 rounded px-2 py-1" />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {!loaded && <p className="p-3 text-xs text-gray-400 italic">Loading…</p>}
            {loaded && filtered.length === 0 && (
              <p className="p-3 text-xs text-gray-500 italic">
                {templates.length === 0
                  ? 'No templates yet. Create some on your Profile page (personal) or the Team page (firm-wide).'
                  : 'No templates match your filter.'}
              </p>
            )}
            {filtered.map(t => (
              <button key={t.id} type="button" onClick={() => pick(t)}
                className="w-full text-left px-3 py-2 hover:bg-primary-50 border-b border-gray-100 last:border-b-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-semibold text-gray-800">{t.label}</span>
                  {t.is_firm_wide && <span className="text-[10px] bg-emerald-100 text-emerald-700 rounded-full px-1.5 py-0.5">firm-wide</span>}
                  {t.activity_code && <span className="text-[10px] bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5 font-mono">{t.activity_code}</span>}
                  {t.use_count > 0 && <span className="text-[10px] text-gray-400 ml-auto">used {t.use_count}×</span>}
                </div>
                <div className="text-[11px] text-gray-600 mt-0.5 truncate">{t.body}</div>
                {value && value.trim() && (
                  <div className="text-[10px] text-amber-700 mt-0.5 italic">Will replace current description</div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// NarrativeAutocomplete (G11) — typing-aware suggestion dropdown.
//
// Usage: replace a plain <textarea> for the description with
//   <NarrativeAutocomplete
//     value={form.description}
//     onChange={(v) => setForm({...form, description: v})}
//     activityCode={form.activity_code}
//     textareaProps={{ id, rows, placeholder, className, required }} />
//
// The component owns the textarea (so it can attach keydown listeners
// and position the dropdown), but pushes value + change back to the
// parent for full controlled behavior.  `textareaProps` are spread onto
// the underlying <textarea>.
//
// Behavior:
//   - Templates fetched lazily on first ≥3-char threshold.
//   - Suggestions filter case-insensitively against label + body.
//   - Templates matching the current activity_code sort first, then by
//     use_count DESC, then by label ASC.
//   - Top 5 suggestions rendered.
//   - Filter is debounced 150ms after typing stops.
//   - Keyboard: ArrowDown/ArrowUp navigate; Tab/Enter accept the
//     highlighted suggestion (replaces textarea value + fires POST
//     /:id/use); Escape dismisses AND remembers the value at dismissal
//     so the dropdown doesn't immediately reopen on the next keystroke.
//   - Reopens after Escape only if the value diverges materially from
//     the dismissed value: length delta > 5 OR the last 3 chars aren't
//     a prefix of the dismissed remainder.
//   - Skip on coarse-pointer (mobile) devices — the on-screen keyboard
//     already surfaces suggestions.
//   - Skip when a paste of >100 chars lands (probably not typing).
//   - Skip suggesting a template whose body === current value (avoid
//     "accept what you already have").
// ---------------------------------------------------------------------------

const MIN_CHARS = 3
const MAX_SUGGESTIONS = 5
const DEBOUNCE_MS = 150
const PASTE_THRESHOLD = 100
const REOPEN_LENGTH_DELTA = 5

function isCoarsePointer () {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  try { return window.matchMedia('(pointer: coarse)').matches } catch { return false }
}

function scoreTemplate (t, activityCode) {
  // Higher = better.  Activity-code match wins; then use_count.
  let s = 0
  if (activityCode && t.activity_code === activityCode) s += 1000
  s += Math.min(999, Number(t.use_count) || 0)
  return s
}

function filterAndRank (templates, query, activityCode) {
  const q = (query || '').toLowerCase()
  if (!q) return []
  const matches = templates.filter(t => {
    if ((t.body || '').trim().toLowerCase() === q) return false // exact-match: skip
    return (t.label || '').toLowerCase().includes(q) || (t.body || '').toLowerCase().includes(q)
  })
  matches.sort((a, b) => {
    const sd = scoreTemplate(b, activityCode) - scoreTemplate(a, activityCode)
    if (sd !== 0) return sd
    return (a.label || '').localeCompare(b.label || '')
  })
  return matches.slice(0, MAX_SUGGESTIONS)
}

export function NarrativeAutocomplete ({ value, onChange, activityCode, textareaProps = {} }) {
  const [templates, setTemplates] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [debounced, setDebounced] = useState(value || '')
  const [highlight, setHighlight] = useState(0)
  const dismissedForValue = useRef(null)
  const wrapRef = useRef(null)
  const textareaRef = useRef(null)
  const coarse = useMemo(() => isCoarsePointer(), [])

  // Debounce the value we filter against.  Prevents the dropdown from
  // recomputing on every keystroke of a long paragraph.
  useEffect(() => {
    const h = setTimeout(() => setDebounced(value || ''), DEBOUNCE_MS)
    return () => clearTimeout(h)
  }, [value])

  // Lazy-load templates the first time the value crosses MIN_CHARS.
  useEffect(() => {
    if (coarse) return
    if (loaded || loading) return
    if ((value || '').length < MIN_CHARS) return
    setLoading(true)
    api.get('/api/narrative-templates', {
      params: activityCode ? { activity_code: activityCode } : {}
    })
      .then(r => setTemplates(r.data.templates || []))
      .catch(() => setTemplates([]))
      .finally(() => { setLoaded(true); setLoading(false) })
  }, [value, activityCode, loaded, loading, coarse])

  // Click-outside dismisses the dropdown WITHOUT setting dismissedForValue
  // (so the next keystroke re-opens naturally).
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        dismissedForValue.current = value || ''
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [value])

  const rawSuggestions = useMemo(
    () => filterAndRank(templates, debounced, activityCode),
    [templates, debounced, activityCode]
  )

  // Should we show the dropdown right now?
  const suggestions = coarse ? [] : rawSuggestions
  const dismissed = dismissedForValue.current
  let shouldShow = suggestions.length > 0 && (value || '').length >= MIN_CHARS
  if (shouldShow && dismissed != null) {
    // Reopen only if value diverges materially from dismissed.
    const delta = Math.abs((value || '').length - dismissed.length)
    const diverged = delta > REOPEN_LENGTH_DELTA || !((value || '').startsWith(dismissed) || dismissed.startsWith(value || ''))
    if (!diverged) shouldShow = false
  }

  // Reset highlight when the visible set changes.
  useEffect(() => { setHighlight(0) }, [suggestions.length, shouldShow])

  const accept = (t) => {
    if (!t) return
    onChange(t.body)
    dismissedForValue.current = t.body // don't immediately re-suggest against the same body
    api.post(`/api/narrative-templates/${t.id}/use`).catch(() => {})
    // return focus to the textarea after accepting so typing continues
    setTimeout(() => textareaRef.current?.focus(), 0)
  }

  const dismiss = () => { dismissedForValue.current = value || '' }

  const onKeyDown = (e) => {
    // Chain to caller's onKeyDown first if provided.
    if (typeof textareaProps.onKeyDown === 'function') textareaProps.onKeyDown(e)
    if (e.defaultPrevented) return
    if (!shouldShow) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight(h => Math.min(suggestions.length - 1, h + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight(h => Math.max(0, h - 1))
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      // Only intercept Enter/Tab if there's a highlighted suggestion.
      // Enter without shift shouldn't trap normal newline behavior in a
      // <textarea> when nothing is suggested.
      e.preventDefault()
      accept(suggestions[highlight])
    } else if (e.key === 'Escape') {
      e.preventDefault()
      dismiss()
    }
  }

  const onPaste = (e) => {
    if (typeof textareaProps.onPaste === 'function') textareaProps.onPaste(e)
    if (e.defaultPrevented) return
    const pasted = e.clipboardData?.getData?.('text') || ''
    if (pasted.length > PASTE_THRESHOLD) {
      // A big paste is almost never a "starting to type" moment; dismiss.
      dismissedForValue.current = (value || '') + pasted
    }
  }

  const onChangeInner = (e) => {
    onChange(e.target.value)
    if (typeof textareaProps.onChange === 'function') textareaProps.onChange(e)
  }

  // Strip our own handlers back out of textareaProps so we don't double-attach.
  const {
    onKeyDown: _kd, onPaste: _p, onChange: _oc, value: _v, ref: _r,
    ...restTextareaProps
  } = textareaProps

  return (
    <div ref={wrapRef} className="relative">
      <textarea
        ref={textareaRef}
        value={value || ''}
        onChange={onChangeInner}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        aria-autocomplete="list"
        aria-expanded={shouldShow}
        aria-controls="narrative-suggestions"
        {...restTextareaProps}
      />
      {shouldShow && (
        <div
          id="narrative-suggestions"
          role="listbox"
          className="absolute z-40 mt-1 left-0 right-0 max-w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="px-2 py-1 text-[10px] text-gray-500 bg-gray-50 border-b border-gray-100">
            Suggested templates — ↓↑ to move, Tab/Enter to accept, Esc to dismiss
          </div>
          <div className="max-h-64 overflow-y-auto">
            {suggestions.map((t, i) => (
              <button
                key={t.id}
                type="button"
                role="option"
                aria-selected={i === highlight}
                onMouseDown={(e) => { e.preventDefault(); accept(t) }}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 ${
                  i === highlight ? 'bg-primary-100' : 'hover:bg-primary-50'
                }`}
              >
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-semibold text-gray-800">{t.label}</span>
                  {t.is_firm_wide && (
                    <span className="text-[10px] bg-emerald-100 text-emerald-700 rounded-full px-1.5 py-0.5">firm-wide</span>
                  )}
                  {t.activity_code && (
                    <span className="text-[10px] bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5 font-mono">
                      {t.activity_code}
                    </span>
                  )}
                  {Number(t.use_count) > 0 && (
                    <span className="text-[10px] text-gray-400 ml-auto">{t.use_count}×</span>
                  )}
                </div>
                <div className="text-[11px] text-gray-600 mt-0.5 line-clamp-2">
                  {(t.body || '').slice(0, 160)}
                  {(t.body || '').length > 160 ? '…' : ''}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
